import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box, Container, Typography, Link, Stack, Divider, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';

import PublicLayout from '../../components/Layout/PublicLayout';

// Public legal pages (Terms, Privacy, Data deletion). These URLs go into
// the Meta / Google / Pinterest / X / LinkedIn / TikTok developer consoles,
// so they must render for logged-out visitors and reviewers too — App.js
// serves them ahead of the auth check. Keep the facts here in step with what
// the app really does: the scopes in routes/socialAccounts.js (PLATFORMS
// below), AES-256-GCM token encryption (utils/encryption.js), disconnect
// deleting the social_accounts row, and account deletion by email request.
const APP_NAME = 'Mom N Kidz SocialFlow';
const OPERATOR = 'Digital Sensy';
const CONTACT_EMAIL = 'digitalsensy@gmail.com';
const SITE_URL = 'https://socialflow.digitalsensy.com';
const LAST_UPDATED = 'October 20, 2026';

const NAV = [
  { to: '/terms', label: 'Terms of Service' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/data-deletion', label: 'Data Deletion' },
];

// One row per platform the app can connect: the exact permissions requested
// and what they are used for. Shown on the Privacy Policy.
const PLATFORMS = [
  {
    name: 'Facebook Pages',
    scopes: 'pages_show_list, pages_read_engagement, pages_manage_posts',
    use: 'List the Pages you manage, read basic Page details, and publish the posts you create or schedule to the Page you choose.',
  },
  {
    name: 'Instagram (professional accounts)',
    scopes: 'instagram_basic, instagram_content_publish, pages_show_list, pages_read_engagement',
    use: 'Find the Instagram professional account linked to your Facebook Page, show its username and picture, and publish the images and captions you create.',
  },
  {
    name: 'Threads',
    scopes: 'threads_basic, threads_content_publish',
    use: 'Show your Threads profile and publish the posts you create.',
  },
  {
    name: 'YouTube (Google)',
    scopes: 'youtube, youtube.force-ssl, yt-analytics.readonly',
    use: 'Show your channel name and picture, upload the videos you choose with the title and description you enter, and show basic performance numbers for videos published through the Service.',
  },
  {
    name: 'Google Business Profile',
    scopes: 'business.manage',
    use: 'List the business locations you manage and publish the updates you create.',
  },
  {
    name: 'Google Ads',
    scopes: 'adwords',
    use: 'Show the Ads accounts you connect and the campaign information you choose to manage in the Service.',
  },
  {
    name: 'Pinterest',
    scopes: 'user_accounts:read, boards:read, boards:write, pins:read, pins:write',
    use: 'Show your profile, list your boards so you can pick one, and create the Pins you design.',
  },
  {
    name: 'X (Twitter)',
    scopes: 'users.read, tweet.read, tweet.write, offline.access',
    use: 'Show your profile, publish the posts you create, and stay connected without asking you to sign in again each time.',
  },
  {
    name: 'LinkedIn',
    scopes: 'openid, profile, email, w_member_social',
    use: 'Show your name and picture and publish the posts you create to your profile.',
  },
  {
    name: 'TikTok',
    scopes: 'user.info.basic, video.publish',
    use: 'Show your profile and publish the videos you choose.',
  },
  {
    name: 'Quora',
    scopes: 'None: no Quora login or password is stored',
    use: 'Quora has no posting API, so we store only the public profile or Space link you enter. You copy the text and publish it on Quora yourself, then paste back the link so the post shows as published.',
  },
  {
    name: 'WordPress and Bluesky',
    scopes: 'Application password you create on that site',
    use: 'Publish the articles or posts you create. The application password is stored encrypted and can be revoked on your site at any time.',
  },
];

const Mail = () => <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>;
const Ext = ({ href, children }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer">{children || href}</Link>
);
const To = ({ to, children }) => <Link component={RouterLink} to={to}>{children}</Link>;

const Section = ({ id, title, children }) => (
  <Box component="section" id={id} sx={{ mb: 4, scrollMarginTop: 24 }}>
    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
      {title}
    </Typography>
    <Box sx={{ '& p': { mt: 0, mb: 1.5 }, '& ul': { mt: 0, mb: 1.5, pl: 3 }, '& li': { mb: 0.75 }, '& code': { fontSize: '0.85em' }, color: 'text.primary', lineHeight: 1.7 }}>
      {children}
    </Box>
  </Box>
);

const LegalLayout = ({ title, children }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = `${title} · ${APP_NAME}`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <PublicLayout>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 3 }}>
          {NAV.map((item) => (
            <Chip
              key={item.to}
              component={RouterLink}
              to={item.to}
              clickable
              label={item.label}
              color={pathname === item.to ? 'primary' : 'default'}
              variant={pathname === item.to ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: {LAST_UPDATED}
        </Typography>
        {children}
        <Divider sx={{ my: 4 }} />
        <Typography variant="body2" color="text.secondary">
          {APP_NAME} is operated by {OPERATOR}. Questions about this page: <Mail />
        </Typography>
      </Container>
    </PublicLayout>
  );
};

export const TermsOfService = () => (
  <LegalLayout title="Terms of Service">
    <Section id="agreement" title="1. Agreement">
      <p>
        These Terms of Service ("Terms") govern your use of {APP_NAME} at {SITE_URL} (the "Service"),
        operated by {OPERATOR} ("we", "us", "our"). By creating an account or using the Service you agree
        to these Terms and to our <To to="/privacy">Privacy Policy</To>. If you do not agree, do not use the Service.
      </p>
    </Section>

    <Section id="service" title="2. The Service">
      <p>
        {APP_NAME} lets you create, schedule and publish content to social media accounts and websites you own
        or are authorized to manage — including Facebook Pages, Instagram professional accounts, Threads, YouTube,
        Google Business Profile, Pinterest, X (Twitter), LinkedIn, TikTok, WordPress and Bluesky — and to manage
        brands, media and team members in one workspace.
      </p>
    </Section>

    <Section id="account" title="3. Your account">
      <p>
        You must give accurate information when you sign up, keep your password secure, and be at least
        18 years old (or the age of majority where you live). You are responsible for all activity under your
        account and for the team members you invite to your brands. Tell us immediately at <Mail /> if you
        suspect unauthorized access.
      </p>
    </Section>

    <Section id="third-party" title="4. Connecting third-party platforms">
      <p>
        When you connect a platform, you authorize the Service to access it through that platform's official API,
        only with the permissions you approve on the platform's own consent screen, and only to provide the features
        you use. We never publish anything you have not created, scheduled or approved. You must only connect
        accounts you own or are authorized to manage.
      </p>
      <p>
        Your use of each platform through the Service remains subject to that platform's own terms. By connecting
        or using a platform you also agree to:
      </p>
      <ul>
        <li>YouTube: <Ext href="https://www.youtube.com/t/terms">YouTube Terms of Service</Ext> and the <Ext href="https://policies.google.com/privacy">Google Privacy Policy</Ext></li>
        <li>Facebook, Instagram and Threads: <Ext href="https://www.facebook.com/terms.php">Meta Terms of Service</Ext>, <Ext href="https://help.instagram.com/581066165581870">Instagram Terms of Use</Ext> and <Ext href="https://help.instagram.com/769983657850450">Threads Terms of Use</Ext></li>
        <li>Pinterest: <Ext href="https://policy.pinterest.com/en/terms-of-service">Pinterest Terms of Service</Ext></li>
        <li>X (Twitter): <Ext href="https://x.com/en/tos">X Terms of Service</Ext></li>
        <li>LinkedIn: <Ext href="https://www.linkedin.com/legal/user-agreement">LinkedIn User Agreement</Ext></li>
        <li>TikTok: <Ext href="https://www.tiktok.com/legal/page/global/terms-of-service/en">TikTok Terms of Service</Ext></li>
        <li>Quora: <Ext href="https://www.quora.com/about/tos">Quora Terms of Service</Ext> (you publish Quora content yourself; we never log in to Quora for you)</li>
        <li>Google Business Profile and Google Ads: <Ext href="https://policies.google.com/terms">Google Terms of Service</Ext></li>
      </ul>
      <p>
        You can disconnect a platform at any time under Integrations, or revoke our access in the platform's own
        settings (see <To to="/data-deletion">Data Deletion</To>). We are not responsible for a platform's outages,
        API or policy changes, or decisions to reject content or restrict an account.
      </p>
    </Section>

    <Section id="content" title="5. Your content">
      <p>
        You keep all ownership of the text, images, videos and other content you upload or publish through the
        Service ("Your Content"). You give us a limited, non-exclusive licence to host, process and transmit Your
        Content only as needed to provide the Service to you, ending when it is deleted. You confirm you have all
        the rights needed to publish Your Content, including consent from anyone who appears in it.
      </p>
    </Section>

    <Section id="acceptable-use" title="6. Acceptable use">
      <p>You agree not to use the Service to:</p>
      <ul>
        <li>publish content that is illegal, deceptive, hateful, harassing, sexually explicit, or infringes anyone's rights;</li>
        <li>send spam, run fake engagement, or break the rules or developer policies of any connected platform;</li>
        <li>access accounts, brands or data that are not yours, or scrape data from any platform;</li>
        <li>disrupt, overload, probe or reverse-engineer the Service.</li>
      </ul>
    </Section>

    <Section id="ai" title="7. AI-generated suggestions">
      <p>
        The Service offers optional AI tools that suggest captions, hashtags, post ideas and articles from the text
        you enter. Suggestions may be inaccurate or unsuitable. Review them before publishing; you are responsible for
        anything you publish. We do not send data received from connected platforms to AI providers.
      </p>
    </Section>

    <Section id="privacy" title="8. Privacy and data protection">
      <p>
        Our <To to="/privacy">Privacy Policy</To> explains what personal data we collect, why, and your rights,
        including under the EU/UK GDPR. You can ask us to delete your data at any time — see{' '}
        <To to="/data-deletion">Data Deletion</To>.
      </p>
    </Section>

    <Section id="termination" title="9. Suspension and termination">
      <p>
        You may stop using the Service at any time and ask us to delete your account. We may suspend or close an
        account that breaks these Terms, misuses a connected platform, or puts the Service or other users at risk.
        When an account is closed, we delete its data as described in the Privacy Policy.
      </p>
    </Section>

    <Section id="disclaimer" title="10. Disclaimer">
      <p>
        The Service is provided "as is" and "as available". We work to keep it reliable, but we do not guarantee it
        will be uninterrupted or error-free, or that every scheduled post will be published, because publishing
        depends on third-party platforms.
      </p>
    </Section>

    <Section id="liability" title="11. Limitation of liability">
      <p>
        To the extent permitted by law, we are not liable for indirect or consequential loss, or for loss of data,
        profits, followers or business opportunities arising from your use of the Service. Nothing in these Terms
        limits liability that cannot be limited by law, or any mandatory rights you have as a consumer under the
        laws of the country where you live (including in the EU and UK).
      </p>
    </Section>

    <Section id="changes" title="12. Changes to these Terms">
      <p>
        We may update these Terms. We will change the "Last updated" date above and, for significant changes,
        notify users by email or in the app before they take effect. Continuing to use the Service after a change
        means you accept it.
      </p>
    </Section>

    <Section id="law" title="13. Governing law">
      <p>
        These Terms are governed by the laws of India, and disputes are subject to the jurisdiction of the courts of
        India. If you are a consumer in the EU or UK, you also keep the protection of the mandatory laws of your
        country and may bring proceedings there.
      </p>
    </Section>

    <Section id="contact" title="14. Contact">
      <p>{OPERATOR} — <Mail /></p>
    </Section>
  </LegalLayout>
);

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy">
    <Section id="who" title="1. Who we are">
      <p>
        {APP_NAME} ({SITE_URL}) is operated by {OPERATOR}, India, which is the data controller for the personal
        data described in this policy. Contact for all privacy matters, including our Grievance Officer under
        India's Digital Personal Data Protection Act, 2023 and data protection requests under the EU/UK GDPR: <Mail />.
      </p>
      <p>
        Where you use the Service to manage a brand on behalf of a client, you are responsible for having the right
        to connect that client's accounts and to share their content with us.
      </p>
    </Section>

    <Section id="collect" title="2. Data we collect">
      <ul>
        <li><strong>Account data:</strong> name, email address, password (stored only as a secure bcrypt hash), profile picture, and your roles in brands and teams.</li>
        <li><strong>Brand and content data:</strong> brands, posts, captions, schedules, templates, comments, and images or videos you upload.</li>
        <li><strong>Connected-platform data:</strong> see section 3 — account ID, name or username, profile picture, follower count, the Pages, boards, channels or locations you can publish to, and the access tokens the platform issues to us.</li>
        <li><strong>Publishing results:</strong> the IDs and links of posts we publish for you, and basic performance numbers where the platform provides them.</li>
        <li><strong>Technical data:</strong> activity logs (for example sign-ins, uploads and publishing actions), IP address and browser information in server logs, and a sign-in token kept in your browser's local storage.</li>
      </ul>
      <p>We do not collect special categories of personal data, and we do not access your private messages, friends lists or contacts on any platform.</p>
    </Section>

    <Section id="platforms" title="3. Data from connected platforms">
      <p>
        You choose which platforms to connect. For each one we request only the permissions below, and use the data
        only for the purpose shown:
      </p>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Platform</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Permissions requested</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>How we use it</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PLATFORMS.map((p) => (
              <TableRow key={p.name}>
                <TableCell sx={{ verticalAlign: 'top', fontWeight: 600 }}>{p.name}</TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}><code>{p.scopes}</code></TableCell>
                <TableCell sx={{ verticalAlign: 'top' }}>{p.use}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <p>
        For every platform: we do not sell platform data, use it for advertising or profiling, share it with data
        brokers, or use it to build or train AI models. Access tokens are encrypted at rest with AES-256-GCM.
      </p>
    </Section>

    <Section id="google" title="4. Google and YouTube user data">
      <p>
        {APP_NAME} uses YouTube API Services. By connecting YouTube you agree to the{' '}
        <Ext href="https://www.youtube.com/t/terms">YouTube Terms of Service</Ext>, and Google's handling of your data
        is described in the <Ext href="https://policies.google.com/privacy">Google Privacy Policy</Ext>.
      </p>
      <p>
        {APP_NAME}'s use and transfer of information received from Google APIs to any other app will adhere to the{' '}
        <Ext href="https://developers.google.com/terms/api-services-user-data-policy">Google API Services User Data Policy</Ext>,
        including the Limited Use requirements. Specifically, Google user data is used only to provide the features
        described in section 3; it is not transferred to others except as needed to provide those features, to comply
        with law, or as part of a merger with notice to you; it is not used for advertising; it is not read by humans
        except with your consent, for security, or to comply with law; and it is not used to develop, improve or train
        generalized AI or machine-learning models.
      </p>
      <p>
        You can revoke our access to your Google account at any time at{' '}
        <Ext href="https://myaccount.google.com/permissions">myaccount.google.com/permissions</Ext>. Revoking access or
        disconnecting YouTube in the Service deletes the stored access tokens.
      </p>
    </Section>

    <Section id="meta" title="5. Facebook, Instagram and Threads data">
      <p>
        Data received from Meta platforms is processed in line with the{' '}
        <Ext href="https://developers.facebook.com/terms">Meta Platform Terms</Ext> and Developer Policies, only to provide
        the features you use. You can remove our app at any time in Facebook Settings &amp; Privacy → Settings →
        Business Integrations, and ask us to delete your data as described on our <To to="/data-deletion">Data Deletion</To> page.
      </p>
    </Section>

    <Section id="use" title="6. How we use data and our legal bases (GDPR)">
      <ul>
        <li><strong>To provide the Service</strong> — your account, scheduling, publishing, media library and team features. Legal basis: performance of our contract with you.</li>
        <li><strong>To connect and publish to platforms you choose.</strong> Legal basis: your consent, given on each platform's consent screen, which you can withdraw at any time by disconnecting.</li>
        <li><strong>AI suggestions</strong>, when you choose to use them. Legal basis: your request (contract).</li>
        <li><strong>Service emails</strong> such as verification, password resets and notifications. Legal basis: contract.</li>
        <li><strong>Security, abuse prevention and fixing problems.</strong> Legal basis: our legitimate interest in keeping the Service safe.</li>
        <li><strong>Legal obligations</strong>, such as responding to lawful requests. Legal basis: legal obligation.</li>
      </ul>
      <p>We do not use automated decision-making that has legal or similarly significant effects on you.</p>
    </Section>

    <Section id="sharing" title="7. Who we share data with">
      <p>We share personal data only with service providers that process it for us under contract, and only what they need:</p>
      <ul>
        <li><strong>Hostinger</strong> — hosting of our servers and database.</li>
        <li><strong>Our email delivery provider</strong> — to send service emails.</li>
        <li><strong>Anthropic</strong> (Claude) — only the text you enter into the AI tools, to generate suggestions. No connected-platform data is sent.</li>
        <li><strong>The platforms you connect</strong> — the content you choose to publish is sent to that platform.</li>
      </ul>
      <p>
        We may disclose data if required by law or to protect rights and safety. We do not sell or "share" personal
        information for cross-context behavioural advertising, as those terms are defined in the California Consumer
        Privacy Act (CCPA/CPRA).
      </p>
    </Section>

    <Section id="transfers" title="8. International transfers">
      <p>
        We are based in India and our providers may process data in other countries, including the United States and
        the European Union. When personal data from the EU, EEA, UK or Switzerland is transferred to a country without
        an adequacy decision, we rely on appropriate safeguards such as the European Commission's Standard Contractual
        Clauses (and the UK Addendum). Contact us for a copy of the relevant safeguards.
      </p>
    </Section>

    <Section id="retention" title="9. How long we keep data">
      <ul>
        <li><strong>Connected-platform tokens and account details:</strong> until you disconnect the platform or revoke access, then deleted immediately.</li>
        <li><strong>Account, brand and content data:</strong> while your account is active; deleted within 30 days after you ask us to delete your account.</li>
        <li><strong>Server and activity logs:</strong> up to 90 days, unless needed longer to investigate a security issue.</li>
        <li>Data we must keep by law (for example billing records) is kept only for the period the law requires.</li>
      </ul>
    </Section>

    <Section id="security" title="10. Security">
      <p>
        All traffic uses HTTPS. Passwords are hashed with bcrypt, platform access tokens and application passwords are
        encrypted at rest with AES-256-GCM, and access to production systems is restricted. If a personal data breach
        is likely to put your rights at risk, we will notify you and the relevant authorities as the law requires
        (under GDPR, the supervisory authority within 72 hours).
      </p>
    </Section>

    <Section id="rights" title="11. Your rights">
      <p>Depending on where you live (including under the EU/UK GDPR, the CCPA/CPRA and India's DPDP Act), you have the right to:</p>
      <ul>
        <li>access the personal data we hold about you and get a copy of it;</li>
        <li>correct inaccurate or incomplete data;</li>
        <li>have your data deleted ("right to be forgotten");</li>
        <li>restrict or object to our processing, including processing based on legitimate interests;</li>
        <li>receive your data in a portable, machine-readable format;</li>
        <li>withdraw consent at any time (for platforms, by disconnecting them), without affecting earlier processing;</li>
        <li>not be discriminated against for exercising your rights;</li>
        <li>nominate another person to exercise your rights if you die or become incapacitated (India).</li>
      </ul>
      <p>
        To exercise any right, email <Mail />. We will verify your identity and respond within one month (30 days),
        extendable by two further months for complex requests, in which case we will tell you why. You also have the
        right to lodge a complaint with a data protection supervisory authority — in the EU, the authority in your
        country of residence or work; in the UK, the Information Commissioner's Office (ico.org.uk); in India, the
        Data Protection Board of India.
      </p>
    </Section>

    <Section id="cookies" title="12. Cookies and local storage">
      <p>
        {APP_NAME} does not use advertising or analytics tracking cookies. We use your browser's local storage only for
        what the Service needs to work: your sign-in token and interface preferences such as a collapsed sidebar. You
        can clear these at any time by signing out or clearing your browser data. When you connect a platform, its
        own login page may set cookies under that platform's policy.
      </p>
    </Section>

    <Section id="children" title="13. Children">
      <p>
        The Service is for business use and is not intended for anyone under 18. We do not knowingly collect personal
        data from children; if you believe a child has given us data, contact us and we will delete it.
      </p>
    </Section>

    <Section id="changes" title="14. Changes to this policy">
      <p>
        We may update this policy and will change the "Last updated" date above. For significant changes we will
        notify users by email or in the app before they take effect.
      </p>
    </Section>

    <Section id="contact" title="15. Contact">
      <p>{OPERATOR} (data controller and Grievance Officer) — <Mail /></p>
    </Section>
  </LegalLayout>
);

export const DataDeletion = () => (
  <LegalLayout title="Data Deletion Instructions">
    <Section id="disconnect" title="1. Disconnect a platform (immediate)">
      <p>To stop {APP_NAME} accessing a platform and delete everything we store for it (access tokens, account ID, name, picture and follower count):</p>
      <ul>
        <li>Sign in to {APP_NAME} and open <strong>Integrations</strong> for your brand.</li>
        <li>Click <strong>Disconnect</strong> on the account. The stored data for that connection is deleted immediately.</li>
      </ul>
    </Section>

    <Section id="revoke" title="2. Revoke access on the platform">
      <p>You can also remove our access directly from each platform, without signing in to {APP_NAME}:</p>
      <ul>
        <li><strong>Facebook, Instagram, Threads:</strong> Facebook → Settings &amp; Privacy → Settings → Business Integrations (or Apps and Websites) → select {APP_NAME} → Remove.</li>
        <li><strong>YouTube, Google Business Profile, Google Ads:</strong> <Ext href="https://myaccount.google.com/permissions">myaccount.google.com/permissions</Ext> → select {APP_NAME} → Remove access.</li>
        <li><strong>Pinterest:</strong> Settings → Security and logins → Apps → Revoke access.</li>
        <li><strong>X (Twitter):</strong> Settings → Security and account access → Apps and sessions → Connected apps → Revoke app permissions.</li>
        <li><strong>LinkedIn:</strong> Settings → Data privacy → Permitted services → Remove.</li>
        <li><strong>TikTok:</strong> Settings and privacy → Security → Manage app permissions → Remove.</li>
        <li><strong>WordPress:</strong> Users → Profile → Application Passwords → Revoke. <strong>Bluesky:</strong> Settings → Privacy and security → App passwords → Delete.</li>
      </ul>
      <p>Once access is revoked, the stored tokens stop working; disconnect the account in Integrations (or ask us) to delete them from our database too.</p>
    </Section>

    <Section id="account" title="3. Delete your whole account and all data">
      <p>
        Email <Mail /> from the address you signed up with, with the subject <strong>"Data deletion request"</strong>.
        Please say whether you want your whole account deleted or only certain data. We will:
      </p>
      <ul>
        <li>verify the request comes from the account owner;</li>
        <li>delete your account, brands, posts, schedules, uploaded media, connected-platform data and tokens within 30 days;</li>
        <li>email you a confirmation, with a reference number, once deletion is complete.</li>
      </ul>
      <p>
        Content already published to a platform stays on that platform — delete it there. Backups containing your
        data are overwritten within 30 days of deletion. Data we are legally required to keep (for example billing
        records) is kept only for the period the law requires and then deleted.
      </p>
    </Section>

    <Section id="rights" title="4. Your privacy rights">
      <p>
        Data deletion is one of your rights under the EU/UK GDPR, the CCPA/CPRA and India's DPDP Act. See our{' '}
        <To to="/privacy">Privacy Policy</To> for all your rights and how to complain to a data protection authority.
      </p>
    </Section>
  </LegalLayout>
);

export const LEGAL_PATHS = NAV.map((item) => item.to);
